import React, { FC, useEffect, useState } from "react";
import axios from "axios";
import {
  inventoryIndyConstants,
  inventoryOtherConstants,
} from "../constants/statsConstants";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import {
  FetchTitleListInventorySalesRecord,
  inventorySalesDataInterface,
} from "../../../redux/reducers/titleListInventorySalesReducer";
import moment from "moment";
import { FaExternalLinkAlt } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { API } from "../../../constants/API";
import Loading from "../../../common-components/Loader";
import IndyBoUnitsModal from "../modals/IndyBoUnitsModal";
import PrintDetailsModal from "../modals/PrintDetailsModal";
import PrintHistoryModal from "../modals/PrintHistoryModal";
import { OptionType } from "../../TMM/commonInterfaceTypes";
import { clearBoUnitsDetails } from "../../../redux/reducers/boUnitsDetailsReducer";
import { Control, UseFormWatch } from "react-hook-form";
import {
  formatCurrency,
  formatCurrencyOne,
} from "../../../common-components/commonFunctions";

interface salesDetailsProps {
  selectedIsbn?: string | null;
  selectedIsbnOption?: OptionType | null;
  selectedTitleOption?: OptionType | null;
  imageSrc?: string;
  control: Control<any>;
  watch: UseFormWatch<any>;
  isbnNumberOptions: OptionType[];
  isbnTitleOptions: OptionType[];
  changeIsbnInfo: (event: any) => void;
  isFirefox: boolean;
  hasMoreIsbn: boolean;
}

export interface PrintDetailsData {
  ISBN?: string;
  // Latest print status info
  DATE?: string;
  WHSE_DUE_DATE?: string | { value?: string };
  NEXT_DUE_QTY?: number;
  IN_PRINT?: number;
  US_PRICE?: number | string | null;
  CANADIAN_PRICE?: number | string | null;
  // Print history info
  PRINT_FILEDATE?: string;
  PRINTING?: string;
  PRINT_WHSE_DUE_DATE?: string | { value?: string };
  PRINT_QTY?: string | number;
  TOTAL_RECEIVED_QTY?: string | number;
}

export interface PrintHistoryRow {
  PRINTING?: string | number;
  WHSE_DUE_DATE?: string | { value?: string };
  PRINT_QTY?: string | number;
  TOTAL_RECEIVED_QTY?: string | number;
  US_PRICE?: number | string | null;
  CANADIAN_PRICE?: number | string | null;
}

export interface PrintPricingData {
  EAN?: string;
  US_PRICE?: number | string | null;
  CANADIAN_PRICE?: number | string | null;
}

const InventorySalesContainer: React.FC<salesDetailsProps> = ({
  selectedIsbn,
  selectedIsbnOption,
  selectedTitleOption,
  imageSrc,
  control,
  watch,
  isbnNumberOptions,
  isbnTitleOptions,
  changeIsbnInfo,
  isFirefox,
  hasMoreIsbn,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams();
  const inventorySales = useSelector(
    (state: RootState) => state.titleSalesInventoryReducer.data
  );
  const DetailData = useSelector(
    (state: RootState) => state.detailPageReducer.data
  );
  const DetailDataLoading = useSelector(
    (state: RootState) => state.detailPageReducer.isLoading
  );
  const [printDetails, setPrintDetails] = useState<PrintDetailsData | null>(null);
  const [printDetailsLoading, setPrintDetailsLoading] = useState<boolean>(false);
  const [hasNoData, setHasNoData] = useState<boolean>(false);
  const [isIndyModalOpen, setIsIndyModalOpen] = useState<boolean>(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState<boolean>(false);
  const [isPrintHistoryModalOpen, setIsPrintHistoryModalOpen] =
    useState<boolean>(false);
  const [printHistory, setPrintHistory] = useState<PrintHistoryRow[]>([]);
  const [printHistoryAllRows, setPrintHistoryAllRows] = useState<PrintHistoryRow[]>([]);
  const [printHistoryLoading, setPrintHistoryLoading] = useState<boolean>(false);
  const [printHistoryError, setPrintHistoryError] = useState<string | null>(null);
  const [printHistoryRecordCount, setPrintHistoryRecordCount] =
    useState<number>(0);
  const [indyBoUnitsValue, setIndyBoUnitsValue] = useState<number | null>(null);

  const getDateString = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value === "object" && value.value) return String(value.value);
    return null;
  };

  // Get ISBN from multiple sources: prop, DetailData fields, or URL params
  // Check if values are strings and not empty
  const getValidIsbn = (value: any): string | null => {
    if (!value) return null;
    const str = String(value).trim();
    return str.length > 0 ? str : null;
  };

  const isbnToUse =
    getValidIsbn(selectedIsbn) ||
    getValidIsbn(DetailData?.EAN) ||
    getValidIsbn(DetailData?.ISBN) ||
    getValidIsbn(DetailData?.PARENT_ISBN) ||
    getValidIsbn(params.id) ||
    null;

  // Fetch Indy BO Units using the dedicated BO endpoint
  useEffect(() => {
    const fetchIndyBoUnits = async () => {
      const isbnString = isbnToUse ? String(isbnToUse).trim() : "";

      if (!isbnString) {
        setIndyBoUnitsValue(null);
        return;
      }

      try {
        const response = await axios.post(API.FetchInventorySalesBo, {
          SelectedEbookIsbn: isbnString,
        });

        if (response?.data && typeof response.data.Total_BO_Units === "number") {
          setIndyBoUnitsValue(response.data.Total_BO_Units);
        } else if (
          response?.data &&
          typeof response.data.Total_BO_Units !== "undefined"
        ) {
          const numericValue = Number(response.data.Total_BO_Units);
          setIndyBoUnitsValue(isNaN(numericValue) ? 0 : numericValue);
        } else {
          setIndyBoUnitsValue(null);
        }
      } catch (error) {
        console.error("Error fetching Indy BO Units via fetchTitleInventorySalesBo:", error);
        // Do not block the rest of the inventory data if this call fails
        setIndyBoUnitsValue(null);
      }
    };

    fetchIndyBoUnits();
  }, [isbnToUse]);

  useEffect(() => {
    if (isbnToUse && isbnToUse?.length) {
      dispatch(
        FetchTitleListInventorySalesRecord({ SelectedEbookIsbn: isbnToUse })
      );
    }
  }, [isbnToUse, dispatch]);

  // Fetch print details when ISBN is available
  useEffect(() => {
    const fetchPrintDetails = async () => {
      console.log("=== Print Details API Debug ===");
      console.log("Print Details API - selectedIsbn prop:", selectedIsbn);
      console.log("Print Details API - DetailData:", DetailData);
      console.log("Print Details API - DetailData._id:", DetailData?._id);
      console.log("Print Details API - DetailData.EAN:", DetailData?.EAN);
      console.log("Print Details API - DetailData.ISBN:", DetailData?.ISBN);
      console.log("Print Details API - DetailData.PARENT_ISBN:", DetailData?.PARENT_ISBN);
      console.log("Print Details API - params.id:", params.id);
      console.log("Print Details API - DetailDataLoading:", DetailDataLoading);
      console.log("Print Details API - isbnToUse:", isbnToUse);
      console.log("===============================");

      // Don't call API if DetailData is still loading (unless we already have an ISBN)
      if (DetailDataLoading && !isbnToUse) {
        console.log("Print Details API - DetailData is loading, waiting...");
        setHasNoData(false);
        return;
      }

      // Convert to string and check if it's a valid ISBN (not empty, not just whitespace)
      const isbnString = isbnToUse ? String(isbnToUse).trim() : "";

      if (!isbnString || isbnString.length === 0) {
        console.log("Print Details API - ISBN is empty, skipping API call");
        setPrintDetails(null);
        setHasNoData(false);
        return;
      }

      try {
        setPrintDetailsLoading(true);
        setHasNoData(false);
        console.log("Print Details API - Calling API with ISBN:", isbnString);
        const response = await axios.post(API.fetchPrintDetails, {
          ISBN: isbnString,
        });
        console.log("Print Details API - Response:", response.data);

        // Check if response is successful and has valid data
        if (response.data?.success && response.data?.data && response.data.data !== null) {
          // Check if data object has any meaningful values
          const data = response.data.data;
          const hasValidData =
            data.ISBN ||
            data.WHSE_DUE_DATE ||
            data.NEXT_DUE_QTY !== undefined ||
            data.IN_PRINT !== undefined ||
            data.PRINT_FILEDATE ||
            data.PRINT_QTY ||
            data.TOTAL_RECEIVED_QTY;

          if (hasValidData) {
            setPrintDetails(data);
            setHasNoData(false);
          } else {
            console.log("Print Details API - Data object is empty");
            setPrintDetails(null);
            setHasNoData(true);
          }
        } else {
          console.log("Print Details API - No data in response");
          setPrintDetails(null);
          setHasNoData(true);
        }
      } catch (error: any) {
        console.error("Error fetching print details:", error);
        console.error("Error response:", error.response?.data);
        setPrintDetails(null);
        // If it's a 404 or no data error, show the no data message
        if (error.response?.status === 404 || !error.response?.data?.data) {
          setHasNoData(true);
        } else {
          setHasNoData(false);
        }
      } finally {
        setPrintDetailsLoading(false);
      }
    };

    fetchPrintDetails();
  }, [isbnToUse, selectedIsbn, DetailData, DetailDataLoading, params.id]);

  // Prefetch print history when ISBN is available
  useEffect(() => {
    const fetchPrintHistory = async () => {
      const isbnString = isbnToUse ? String(isbnToUse).trim() : "";

      if (!isbnString) {
        setPrintHistory([]);
        setPrintHistoryAllRows([]);
        setPrintHistoryRecordCount(0);
        setPrintHistoryError(null);
        return;
      }

      try {
        setPrintHistoryLoading(true);
        setPrintHistoryError(null);

        const response = await axios.post(API.fetchPrintHistory, {
          ISBN: isbnString,
        });

        const rows: PrintHistoryRow[] = Array.isArray(response.data?.data)
          ? response.data.data
          : [];

        // Keep a copy of all rows (including the latest print) for export purposes
        setPrintHistoryAllRows(rows);

        setPrintHistoryRecordCount(rows.length);

        // Exclude the row with the greatest PRINTING value (latest printing)
        let filteredRows = rows;
        const numericPrintings = rows
          .map((row) => Number(row.PRINTING))
          .filter((val) => !isNaN(val));

        if (numericPrintings.length > 0) {
          const maxPrinting = Math.max(...numericPrintings);
          filteredRows = rows.filter(
            (row) => Number(row.PRINTING) !== maxPrinting
          );
        }

        setPrintHistory(filteredRows);
      } catch (error: any) {
        console.error("Error fetching print history:", error);
        setPrintHistoryError(
          error?.response?.data?.message ||
          "Failed to fetch print history. Please try again."
        );
        setPrintHistory([]);
        setPrintHistoryAllRows([]);
        setPrintHistoryRecordCount(0);
      } finally {
        setPrintHistoryLoading(false);
      }
    };

    fetchPrintHistory();
  }, [isbnToUse]);

  const handleIndyClick = () => {
    if (!isbnToUse) return;
    setIsIndyModalOpen(true);
  };

  const handleCloseIndyModal = () => {
    setIsIndyModalOpen(false);
    dispatch(clearBoUnitsDetails());
  };

  const handlePrintClick = () => {
    if (!isbnToUse) return;
    setIsPrintModalOpen(true);
  };

  const handleClosePrintModal = () => {
    setIsPrintModalOpen(false);
  };

  const hasMultiplePrintHistoryRecords = printHistoryRecordCount > 1;

  const handlePrintHistoryClick = () => {
    if (!hasMultiplePrintHistoryRecords) return;
    setIsPrintHistoryModalOpen(true);
  };

  const handleClosePrintHistoryModal = () => {
    setIsPrintHistoryModalOpen(false);
  };
  const IndyBoUnits = inventoryIndyConstants
    ? inventoryIndyConstants.map((item) => item.value)
    : [];
  const displayDate = inventorySales.DATE
    ? moment(inventorySales.DATE).format("MM/DD/YYYY")
    : " N/A";

  const invDate = inventorySales.inv_DATE
    ? moment(inventorySales.inv_DATE).format("MM/DD/YYYY")
    : " N/A";

  // Left column fields: Indy Usable, Indy Alloc, Indy Reserved, Indy Other, Indy BO Units
  const leftColumnFields = [
    inventoryIndyConstants.find(item => item.value === "Total_Indy_Usable"),
    inventoryIndyConstants.find(item => item.value === "Total_Indy_Alloc"),
    inventoryIndyConstants.find(item => item.value === "Total_Reserved"),
    inventoryIndyConstants.find(item => item.value === "Total_Indy_Other"),
    inventoryIndyConstants.find(item => item.value === "Total_BO_Units"),
  ].filter(Boolean);

  // Right column fields: UK Usable, AU Usable
  const rightColumnFields = [
    ...inventoryOtherConstants,
  ].filter(Boolean);

  return (
    <div className="w-full my-0 flex flex-col items-center justify-center">
      <div className="w-full bg-[#e31c23] py-2 mb-2 text-[16px] font-bold text-center text-white">
        Inventory Data
      </div>
      <div className="border-2 text-[16px] py-10 flex flex-col relative border-gray-300 w-full">
        <div className="w-full flex justify-around">
          <div className="flex flex-col gap-4">
            <div className="font-bold text-gray-500">HBG Inventory</div>

            <div className="font-bold text-gray-500">Date : {displayDate}</div>
            {leftColumnFields.map((indyData: any) => {
              const isClickable = indyData.value === "Total_BO_Units";
              const rawValue =
                indyData.value === "Total_BO_Units"
                  ? indyBoUnitsValue ??
                  inventorySales[
                  indyData.value as keyof inventorySalesDataInterface
                  ] ??
                  0
                  : inventorySales[
                  indyData.value as keyof inventorySalesDataInterface
                  ] ?? 0;

              return (
                <div
                  key={indyData.value}
                  onClick={() => {
                    if (isClickable) handleIndyClick();
                  }}
                  className={`flex gap-12 justify-between items-center
          ${isClickable ? "cursor-pointer" : ""}`}
                >
                  {/* LEFT SIDE NAME + ARROW */}
                  <div className="flex items-center gap-2">
                    <span
                      className={`font-semibold
              ${isClickable ? "text-blue-600 hover:underline" : "text-gray-800"}`}
                    >
                      {indyData.label}
                    </span>
                    {isClickable && (
                      <FaExternalLinkAlt className="text-red-600 h-[13px] " />

                    )}
                  </div>

                  <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                    {formatCurrencyOne(Number(rawValue ?? 0), 0, "")}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="flex flex-col gap-4">
            <div className="font-bold text-gray-500">Other Inventory</div>

            <div className="font-bold text-gray-500">Date : {invDate}</div>

            {rightColumnFields.map((indyData) => {
              const rawValue = inventorySales[
                indyData?.value as keyof inventorySalesDataInterface
              ]
                ? inventorySales[
                indyData?.value as keyof inventorySalesDataInterface
                ]
                : 0;

              return (
                <div
                  key={indyData?.value}
                  className="flex gap-12 justify-between items-center"
                >
                  <p className="font-semibold">{indyData?.label}</p>
                  <p
                    className={`bg-gray-300 w-[150px] max-w-[150px] h-[45px] max-h-[45px]  items-center flex justify-center`}
                  >
                    {formatCurrencyOne(Number(rawValue ?? 0), 0, "")}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Print Details Section */}
      <div
        className="w-full bg-[#e31c23] py-2 mb-2 text-[16px] font-bold text-center text-white mt-4 flex items-center justify-center gap-2"

      >
        <span>Print Details</span>

      </div>
      <Loading isLoading={printDetailsLoading}>
        <div className="border-2 text-[16px] py-10 flex flex-col relative border-gray-300 w-full">
          {printDetails ? (
            <div className="w-full flex justify-around">


              <div className="flex flex-col gap-4">
                <div className="font-bold text-gray-500">Print Information</div>
                <div className="font-bold text-gray-500">
                  As of : {printDetails.PRINT_FILEDATE ? moment(printDetails?.PRINT_FILEDATE).format("MM/DD/YYYY") : "N/A"}
                </div>

                <div className="flex gap-12 justify-between items-center">
                  <span className="font-semibold text-gray-800">Total In Print</span>
                  <p className="bg-gray-300 w-[150px] max-w-[150px] h-[45px] max-h-[45px] items-center flex justify-center rounded">
                    {formatCurrencyOne(Number(printDetails.IN_PRINT ?? 0), 0, "")}
                  </p>
                </div>

                <div className="flex gap-12 justify-between items-center">
                  <span className="font-semibold text-gray-800">Next WHSE Due Date</span>
                  <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                    {getDateString(printDetails.WHSE_DUE_DATE)
                      ? moment(getDateString(printDetails.WHSE_DUE_DATE)).format(
                        "MM/DD/YYYY"
                      )
                      : "N/A"}
                  </p>
                </div>

                <div className="flex gap-12 justify-between items-center">
                  <span className="font-semibold text-gray-800">
                    Next Due QTY
                  </span>
                  <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                    {formatCurrencyOne(Number(printDetails.NEXT_DUE_QTY ?? 0), 0, "")}
                  </p>
                </div>


              </div>

              <div className="flex flex-col gap-4">
                <div className="font-bold flex items-center gap-2">
                  <button
                    type="button"
                    className={`flex items-center gap-2 ${hasMultiplePrintHistoryRecords
                      ? "text-blue-600 cursor-pointer hover:underline"
                      : "text-gray-500 cursor-default"
                      }`}
                    onClick={
                      hasMultiplePrintHistoryRecords
                        ? handlePrintHistoryClick
                        : undefined
                    }
                  >
                    <span>Print History</span>
                    {hasMultiplePrintHistoryRecords && (
                      <FaExternalLinkAlt className="text-red-600 h-[13px]" />
                    )}
                  </button>
                </div>
                <div className="font-bold text-gray-500">
                  As of :{" "}
                  {printDetails.PRINT_FILEDATE
                    ? moment(printDetails.PRINT_FILEDATE).format("MM/DD/YYYY")
                    : "N/A"}
                </div>

                <div className="flex gap-12 justify-between items-center">
                  <span className="font-semibold text-gray-800">
                    Current Printing Cycle                  </span>
                  <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                    {printDetails.PRINTING ?? "N/A"}
                  </p>
                </div>
                <div className="flex gap-12 justify-between items-center">
                  <span className="font-semibold text-gray-800">
                    USA Price                </span>
                  <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                    {`$ ${
                      printDetails.US_PRICE
                        ? Number(printDetails.US_PRICE).toFixed(2)
                        : "0.00"
                    }`}
                  </p>
                </div>
                <div className="flex gap-12 justify-between items-center">
                  <span className="font-semibold text-gray-800">
                    Canadian Price                  </span>
                  <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                    {`$ ${
                      printDetails.CANADIAN_PRICE
                        ? Number(printDetails.CANADIAN_PRICE).toFixed(2)
                        : "0.00"
                    }`}
                  </p>
                </div>
                

                <div className="flex gap-12 justify-between items-center">
                  <span className="font-semibold text-gray-800">
                    WHSE Date
                  </span>
                  <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                    {getDateString(printDetails.PRINT_WHSE_DUE_DATE)
                      ? moment(
                        getDateString(printDetails.PRINT_WHSE_DUE_DATE)
                      ).format("MM/DD/YYYY")
                      : "N/A"}
                  </p>
                </div>

                <div className="flex gap-12 justify-between items-center">
                  <span className="font-semibold text-gray-800">Print QTY</span>
                  <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                    {formatCurrencyOne(Number(printDetails.PRINT_QTY ?? 0), 0, "")}
                  </p>
                </div>

                <div className="flex gap-12 justify-between items-center">
                  <span className="font-semibold text-gray-800">
                    Total Received QTY
                  </span>
                  <p className="bg-gray-200 rounded w-[150px] h-[45px] flex justify-center items-center">
                    {formatCurrencyOne(Number(printDetails.TOTAL_RECEIVED_QTY ?? 0), 0, "")}
                  </p>
                </div>

              </div>
            </div>
          ) : hasNoData ? (
            <div className="w-full flex flex-col justify-center items-center py-8">
              <p className="text-gray-600 font-semibold text-lg mb-2">
                This ISBN has no data
              </p>
              <p className="text-gray-500 text-sm">
                No print details available for ISBN: {isbnToUse || "N/A"}
              </p>
            </div>
          ) : (
            <div className="w-full flex justify-center items-center py-8">
              <p className="text-gray-500">No print details available</p>
            </div>
          )}
        </div>
      </Loading>

      {isIndyModalOpen && (
        <IndyBoUnitsModal
          isOpen={isIndyModalOpen}
          onClose={handleCloseIndyModal}
          isbn={isbnToUse ? String(isbnToUse) : ""}
          selectedIsbnLabel={
            selectedIsbnOption?.label || DetailData?.EAN || String(isbnToUse)
          }
          selectedTitleLabel={
            selectedTitleOption?.label || (DetailData as any)?.FULL_TITLE || ""
          }
          imageSrc={imageSrc}
          control={control}
          watch={watch}
          isbnNumberOptions={isbnNumberOptions}
          isbnTitleOptions={isbnTitleOptions}
          changeIsbnInfo={changeIsbnInfo}
          isFirefox={isFirefox}
          hasMoreIsbn={hasMoreIsbn}
        />
      )}

      {isPrintModalOpen && (
        <PrintDetailsModal
          isOpen={isPrintModalOpen}
          onClose={handleClosePrintModal}
          imageSrc={imageSrc}
          control={control}
          watch={watch}
          isbnNumberOptions={isbnNumberOptions}
          isbnTitleOptions={isbnTitleOptions}
          changeIsbnInfo={changeIsbnInfo}
          isFirefox={isFirefox}
          hasMoreIsbn={hasMoreIsbn}
          printDetails={printDetails}
          hasNoData={hasNoData}
          isLoading={printDetailsLoading}
          isbnDisplay={isbnToUse}
        />
      )}

      {isPrintHistoryModalOpen && (
        <PrintHistoryModal
          isOpen={isPrintHistoryModalOpen}
          onClose={handleClosePrintHistoryModal}
          rows={printHistory}
          allRows={printHistoryAllRows}
          isLoading={printHistoryLoading}
          error={printHistoryError}
          isbnDisplay={isbnToUse}
          imageSrc={imageSrc}
          control={control}
          watch={watch}
          isbnNumberOptions={isbnNumberOptions}
          isbnTitleOptions={isbnTitleOptions}
          changeIsbnInfo={changeIsbnInfo}
          isFirefox={isFirefox}
          hasMoreIsbn={hasMoreIsbn}
        />
      )}
    </div>
  );
};

export default InventorySalesContainer;
